import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Dialog, DialogContent, DialogTitle, Grid, Typography } from '@mui/material';
import axios from 'axios';
import moment from 'moment';
import EnhancedTableComponent from '../../../components/table';
import EnhancedSnackbar from '../../../components/EnhancedSnackbar';
import VisuallyHidden from '../../../components/VisuallyHidden';
import Config from '../../../config';
import { getCompanyNames } from '../../../helpers';
import DownloadButton from '../../../components/download_button';
import DocLink from '../../../components/document_link';
import CatalogCheckbox from '../../../components/EnhancedCheckbox';
import CatalogEventResults from '../../catalogmanager/containers/catalog/components/catalog_event_results';
import { EventIdColumn } from '../../catalogmanager/containers/catalog/components/catalog_columns';
import { Root, Header, pageContentGap } from '../../../theme';

// EnhancedTable is a JS class wrapped in withRouter; the inferred type only carries
// RouteComponentProps, so we cast to ComponentType<any> to allow our `config`/`template`/etc.
const EnhancedTable = EnhancedTableComponent as React.ComponentType<any>;

interface EventManagementV2Props {
    history?: unknown;
    location?: unknown;
    setTitle?: (title: string) => void;
}

interface TableUrlBuilder {
    filter?: string;
    orderBy?: string;
}

interface EventsTableColumn {
    id: string;
    type?: string;
    filterable?: boolean;
    sortable?: boolean;
    toggleable?: boolean;
    hidden?: boolean;
    label?: string;
    width?: string;
    stopPropagation?: string;
    allowStringOperators?: boolean;
    getOptions?: (...args: any[]) => any;
    template?: (...args: any[]) => any;
}

interface EventsTableFilter {
    filters: Array<{ field: string; operator: string; value: unknown; system?: boolean }>;
    logic: 'and' | 'or';
}

interface EventsTableConfig {
    url: string;
    savedSearchEnabled: boolean;
    columns: EventsTableColumn[];
    order: 'asc' | 'desc';
    orderBy: string;
    keyField: string;
    pageSize: number;
    pageSizes: number[];
    title: string;
    refresh: boolean;
    filterInUrl: boolean;
    filter: EventsTableFilter;
    onGetData: (builder: TableUrlBuilder) => void;
    setSelected: (ids: Array<string | number>) => void;
}

// styled(Typography) strips the OverridableComponent typing so the `component`
// prop isn't visible without this cast.
const PageHeading = Header as React.ComponentType<any>;

function EventManagementV2(props: EventManagementV2Props) {
    const [showError, setShowError] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const tableUrlBuilderRef = useRef<TableUrlBuilder | null>(null);
    const [hasBuilder, setHasBuilder] = useState(false);
    const selectedRef = useRef<Array<string | number>>([]);
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
    const [selectedCount, setSelectedCount] = useState(0);

    const { setTitle } = props;
    useEffect(() => {
        if (setTitle) {
            setTitle('Events');
        }
    }, [setTitle]);

    const markViewed = useCallback(async (ids: Array<string | number>, viewed: boolean) => {
        await axios.get(Config.api + '/odata/Company/Functions.MarkEvents?viewed=' + viewed + '&ids=' + ids.join(','));
    }, []);

    const refresh = useCallback(() => {
        setEventsConfig(prev => ({ ...prev, refresh: !prev.refresh }));
    }, []);

    const onViewChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const id = e.currentTarget.value;
        const checked = e.currentTarget.checked;
        await markViewed([id], checked);
        refresh();
    }, [markViewed, refresh]);

    const onError = useCallback((err: string) => {
        setError(err);
        setShowError(true);
    }, []);

    const markSelected = useCallback(async (viewed: boolean) => {
        // Capture the selection count BEFORE awaiting so the success message reports
        // what the user clicked, not whatever `selectedRef.current` has drifted to after refresh.
        const count = selectedRef.current.length;
        setSubmitting(true);
        try {
            await markViewed(selectedRef.current, viewed);
            refresh();
            setSnackbar({
                open: true,
                message: `${count} event(s) marked ${viewed ? 'viewed' : 'unviewed'}`,
            });
        } catch (e) {
            // Route failures through the existing onError callback so the error Dialog
            // pops as the failure UX (consistent with the rest of the page).
            onError(e instanceof Error ? e.message : String(e));
        } finally {
            setSubmitting(false);
        }
    }, [markViewed, refresh, onError]);

    const exportInfo = useCallback(() => {
        const type = 'xlsx';
        const builder = tableUrlBuilderRef.current || {};
        return {
            href: Config.api + `/odata/Company/Functions.ExportActivityEvents?fileType=${type}&$top=1000`
                + (builder.filter || '') + (builder.orderBy || ''),
            fileName: 'events-' + Math.floor(Date.now() / 1000).toString() + '.' + type,
        };
    }, []);

    // Column templates inside `eventsConfig` capture handlers at mount time.
    // Because each captured handler (e.g. `onViewChange` in the `Viewed` column template)
    // is `useCallback`-stable, the captured identity remains correct across renders —
    // we don't need to memoize `columns` or overlay a derived config.
    const [eventsConfig, setEventsConfig] = useState<EventsTableConfig>({
        url: Config.api + '/odata/Company/VwActivityEvents',
        savedSearchEnabled: true,
        columns: [
            { id: 'EventDate', type: 'date', filterable: true, sortable: true, label: 'Date',
              template: (row: any) => (
                  <Typography>{moment(row.EventDate).format('MM/DD/YYYY hh:mm A')}</Typography>
              ), width: '15em' },
            { id: 'PartnerCompanyName', filterable: true, sortable: true, toggleable: true, label: 'Partner',
              width: '15em', getOptions: getCompanyNames },
            { id: 'DocId', filterable: true, toggleable: true, label: 'ID#', width: '8em',
              template: (row: any) => row.DocId && row.DocType
                  ? <DocLink docType={row.DocType} id={row.DocId}>{row.DocId}</DocLink>
                  : '' },
            { id: 'DocType', hidden: true, label: 'Type' },
            { id: 'EventLevel', filterable: true, sortable: true, toggleable: true, label: 'Severity', width: '6em' },
            { id: 'Summary', filterable: true, toggleable: true, label: 'Summary', allowStringOperators: true },
            { id: 'Viewed', label: 'Viewed', filterable: true, toggleable: true, type: 'bool', sortable: true, width: '5em',
              stopPropagation: 'true',
              template: (row: any) => (
                  <CatalogCheckbox
                      checked={!!row.Viewed}
                      value={row.Id.toString()}
                      onChange={onViewChange}
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  />
              ) },
            { id: 'EventTypeId', hidden: true },
            { id: 'Id', width: '10em', stopPropagation: 'true', filterable: false,
              template: (row: any, column: any, colIndex: number, rowIndex: number, onAddFilter: any, setEventId: any, setEventViewed: any, setEventDialogOpen: any) =>
                  <EventIdColumn
                      row={row}
                      setEventId={setEventId}
                      setEventViewed={setEventViewed}
                      setEventDialogOpen={setEventDialogOpen}
                  /> },
            { id: 'CustomerViewable', hidden: true, filterable: false },
        ],
        order: 'desc',
        orderBy: 'Id',
        keyField: 'Id',
        pageSize: 25,
        pageSizes: [25, 50, 100],
        title: 'Events',
        refresh: false,
        filterInUrl: true,
        filter: { filters: [{ field: 'CustomerViewable', operator: 'eq', value: true, system: true }], logic: 'and' },
        onGetData: (builder: TableUrlBuilder) => {
            tableUrlBuilderRef.current = builder;
            setHasBuilder(true);
        },
        setSelected: (ids: Array<string | number>) => {
            selectedRef.current = ids;
            setSelectedCount(ids.length);
        },
    });

    const bulkActions = (
        <>
            <Button variant="outlined" onClick={() => markSelected(true)} disabled={submitting} style={{ marginRight: '8px' }}>
                Mark Viewed
            </Button>
            <Button variant="outlined" onClick={() => markSelected(false)} disabled={submitting}>
                Mark Unviewed
            </Button>
        </>
    );

    return (
        <main aria-labelledby="page-title">
            <Root style={{ gap: pageContentGap, padding: '24px 14px 24px 14px' }}>
                <Grid container justifyContent="space-between" direction="row" alignItems="center" style={{ width: '100%' }}>
                    <Grid item>
                        <PageHeading id="page-title" component="h1">Events</PageHeading>
                    </Grid>
                    <Grid item style={{ display: 'flex', gap: '8px' }}>
                        <DownloadButton
                            onError={onError}
                            disabled={!hasBuilder}
                            variant="outlined"
                            onClick={exportInfo}
                        >
                            Export
                        </DownloadButton>
                    </Grid>
                </Grid>

                <Grid style={{ width: '100%' }}>
                    <EnhancedTable
                        config={eventsConfig}
                        history={props.history}
                        location={props.location}
                        template={(table: any) => (
                            <CatalogEventResults
                                history={props.history}
                                location={props.location}
                                table={table}
                                config={eventsConfig}
                                setConfig={setEventsConfig}
                                condensed={false}
                                isSelectable={true}
                                bulkActions={bulkActions}
                                showSavedSearch={true}
                                savedSearchTableType="event-management"
                                disableColumnSelector={false}
                            />
                        )}
                    />
                </Grid>

                <Dialog open={showError} onClose={() => setShowError(false)} aria-labelledby="events-error-dialog-title">
                    <DialogTitle id="events-error-dialog-title">Error</DialogTitle>
                    <DialogContent>
                        <Typography>{error}</Typography>
                    </DialogContent>
                </Dialog>

                <EnhancedSnackbar
                    open={snackbar.open}
                    message={snackbar.message}
                    severity="success"
                    autoHideDuration={4000}
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                />

                {/* Polite live region for bulk-action success — the EnhancedSnackbar wraps
                    SnackbarContent (a styled Paper, not Alert), so it does not carry
                    role="alert" semantics on its own. */}
                <VisuallyHidden
                    data-testid="mark-events-status"
                    aria-live="polite"
                    aria-atomic="true"
                >
                    {snackbar.open ? snackbar.message : ''}
                </VisuallyHidden>

                {/* Polite live region for bulk-actions toolbar appearance — closes a11y review #3
                    without churning the shared CatalogManagerTable. Triggered when rows become selected. */}
                <VisuallyHidden
                    data-testid="bulk-actions-status"
                    aria-live="polite"
                    aria-atomic="true"
                >
                    {selectedCount > 0
                        ? `${selectedCount} row${selectedCount === 1 ? '' : 's'} selected. Bulk actions available.`
                        : ''}
                </VisuallyHidden>
            </Root>
        </main>
    );
}

export default EventManagementV2;
